import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation, untranslated } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import type { SlickGrid1Ref, ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import ExternalLinkStatusChip from "./ExternalLinkStatusChip";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { getExternalLinksForResource, reinstateExternalLink, type ExternalLinkListItem } from "./externalLinks.api";
import {
  copyToClipboard,
  formatExpiryForInternal,
  formatShortDate,
  LINK_DIALOG_WIDTH,
  linkStatusLabel,
  permissionLabel,
  revokedByLabel,
  revokedOnLabel,
} from "./externalLinks.helpers";
import type { ExternalLinkTarget } from "./CreateExternalLinkModal";

/** The links grid needs a stable `id` per row; SlickGrid selection is keyed on it. */
type ManageGridRow = ExternalLinkListItem & { id: string };

/** SlickGrid formatters return DOM, not JSX — the standard detached-root bridge (see FileManagerNew). */
const renderInContainer = (element: ReactNode) => {
  const container = document.createElement("div");
  createRoot(container).render(element);
  return container;
};

/**
 * "Manage shared links" — every link on one file or folder, with Edit / View Stats / Copy URL /
 * Revoke.
 *
 * Reached from the context menu, and separate from the row's link badge on purpose: the badge only
 * says that links exist, this is where they are acted on. Lists links created by ANY user, since
 * any full-access user on the item can manage them — not just the original creator.
 */
type ManageExternalLinksModalProps = {
  target: ExternalLinkTarget;
  onClose: () => void;
  onCreate: () => void;
  onEdit: (link: ExternalLinkListItem) => void;
  onStats: (link: ExternalLinkListItem) => void;
  onRevoke: (link: ExternalLinkListItem) => void;
  /**
   * Asks the creator for a fresh expiry before reinstating a link whose stored expiry has already
   * passed (PRD 3.4). Routed through Edit rather than a bespoke date prompt: the same validation, the
   * same picker, and the link becomes active as part of saving.
   */
  onReinstateNeedsExpiry: (link: ExternalLinkListItem) => void;
  /** Bumped by the parent after a create/edit/revoke so the list refetches. */
  refreshToken?: number;
  /** Called after a successful reinstate so the parent can refresh badges and counts. */
  onMutated: () => void;
};

function ManageExternalLinksModal({
  target,
  onClose,
  onCreate,
  onEdit,
  onStats,
  onRevoke,
  onReinstateNeedsExpiry,
  onMutated,
  refreshToken = 0,
}: Readonly<ManageExternalLinksModalProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [links, setLinks] = useState<ExternalLinkListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"active" | "revoked">("active");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reinstating, setReinstating] = useState(false);
  const gridRef = useRef<SlickGrid1Ref<ManageGridRow> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Always fetches BOTH sets: the tab counts have to be right on first paint, and an item's link
    // list is small enough that a second round-trip per tab switch would cost more than it saves.
    const res = await getExternalLinksForResource({
      resourceId: target.resourceId,
      spaceId: target.spaceId,
      includeRevoked: true,
    });
    if (res.status) setLinks(res.links ?? []);
    else
      dispatch(
        showToastNotification({
          content: untranslated(res.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    setLoading(false);
  }, [target.resourceId, target.spaceId, dispatch]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const activeLinks = useMemo(() => links.filter((l) => l.isActive), [links]);
  const revokedLinks = useMemo(() => links.filter((l) => !l.isActive), [links]);
  const rows = tab === "active" ? activeLinks : revokedLinks;
  const selected = useMemo(() => rows.find((l) => l._id === selectedId) ?? null, [rows, selectedId]);

  const handleCopy = useCallback(
    async (link: ExternalLinkListItem) => {
      const ok = await copyToClipboard(link.url);
      dispatch(
        showToastNotification({
          content: ok ? t("Link copied to clipboard") : t("Could not copy the link"),
          type: ok ? TOAST_TYPE_SUCCESS : TOAST_TYPE_ERROR,
        }),
      );
    },
    [dispatch, t],
  );

  const gridRows = useMemo<ManageGridRow[]>(() => rows.map((link) => ({ ...link, id: link._id })), [rows]);

  const handleSelectionChange = () => {
    // getAllSelectedItems can hold undefined slots on some grid paths — filter, never index blindly.
    const items = (gridRef.current?.dataView.getAllSelectedItems() ?? []).filter(Boolean);
    setSelectedId(items[0]?._id ?? null);
  };

  /**
   * The standard grid (QA r2 #3/#9), replacing the hand-rolled CSS table. Columns follow the
   * prototype: Active shows Password / Created On / Created By, Revoked swaps those for
   * Status / Revoked On / Revoked By. Memoised on [tab, t, handleCopy] — a fresh array every render
   * would re-seed the grid's columns each paint.
   */
  const columnDefinitions = useMemo(() => {
    const onActive = tab === "active";
    const nameCell = (link: ManageGridRow) => (
      <span className="group flex min-w-0 items-center gap-2">
        <IconComponent name="Link" width="0.875rem" height="0.875rem" />
        <span className="truncate font-medium">{link.name}</span>
        {/* Hover-revealed like the prototype; cell-scoped hover is what a formatter can own. */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleCopy(link);
          }}
          aria-label={t("Copy URL")}
          title={t("Copy URL")}
          className="flex-none opacity-0 group-hover:opacity-100"
        >
          <IconComponent name="copy" width="0.875rem" height="0.875rem" />
        </button>
      </span>
    );
    return typedColumns<ManageGridRow>([
      {
        id: "name",
        field: "name",
        name: t("Link Name"),
        minWidth: 220,
        sortable: true,
        filterType: "Text",
        formatter: (data: number, params: number, a: unknown, b: unknown, link: ManageGridRow) =>
          renderInContainer(nameCell(link)),
      },
      {
        id: "permission",
        field: "permission",
        name: t("Permission"),
        minWidth: 110,
        sortable: true,
        formatter: (data: number, params: number, a: unknown, b: unknown, link: ManageGridRow) =>
          permissionLabel(t, link.permission),
        _valueGetter: (link: ManageGridRow) => permissionLabel(t, link.permission),
      },
      {
        id: "expiry",
        field: "expiresAt",
        name: t("Expiry"),
        minWidth: 110,
        sortable: true,
        formatter: (data: number, params: number, a: unknown, b: unknown, link: ManageGridRow) =>
          formatExpiryForInternal(t, link.expiresAt),
        _valueGetter: (link: ManageGridRow) => link.expiresAt ?? 0,
      },
      onActive
        ? {
            id: "password",
            field: "hasPassword" as const,
            name: t("Password"),
            minWidth: 90,
            sortable: true,
            formatter: (data: number, params: number, a: unknown, b: unknown, link: ManageGridRow) =>
              link.hasPassword ? t("Yes") : t("No"),
            _valueGetter: (link: ManageGridRow) => (link.hasPassword ? 1 : 0),
          }
        : {
            id: "status",
            field: "isActive" as const,
            name: t("Status"),
            minWidth: 90,
            sortable: true,
            formatter: (data: number, params: number, a: unknown, b: unknown, link: ManageGridRow) =>
              renderInContainer(<ExternalLinkStatusChip link={link} />),
            _valueGetter: (link: ManageGridRow) => linkStatusLabel(t, link),
          },
      {
        id: onActive ? "createdOn" : "revokedOn",
        field: "createdAt",
        name: onActive ? t("Created On") : t("Revoked On"),
        minWidth: 110,
        sortable: true,
        formatter: (data: number, params: number, a: unknown, b: unknown, link: ManageGridRow) =>
          onActive ? formatShortDate(link.createdAt) : revokedOnLabel(link),
        _valueGetter: (link: ManageGridRow) => (onActive ? link.createdAt : revokedOnLabel(link)),
      },
      {
        id: onActive ? "createdBy" : "revokedBy",
        field: "createdByName",
        name: onActive ? t("Created By") : t("Revoked By"),
        minWidth: 130,
        sortable: true,
        formatter: (data: number, params: number, a: unknown, b: unknown, link: ManageGridRow) =>
          onActive ? link.createdByName : revokedByLabel(link),
        _valueGetter: (link: ManageGridRow) => (onActive ? link.createdByName : revokedByLabel(link)),
      },
    ]);
  }, [tab, t, handleCopy]);

  /**
   * "Enable Link" — returns a revoked link to Active with the SAME URL, so recipients who still hold
   * the original link start working again.
   *
   * `expiryRequired` comes back on a SUCCESSFUL response, not an error: the server refuses to
   * reinstate a link whose expiry has already passed because it would be dead on arrival. Handling it
   * as a branch here is why the button opens the Edit form instead of silently doing nothing.
   */
  const handleReinstate = async (link: ExternalLinkListItem) => {
    if (reinstating) return;
    setReinstating(true);
    try {
      const res = await reinstateExternalLink({ linkId: link._id });
      if (!res.status) {
        dispatch(
          showToastNotification({
            content: untranslated(res.errorMessage || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }
      if (res.expiryRequired) {
        dispatch(
          showToastNotification({
            content: t("This link's expiry date has passed. Set a new one to enable it."),
            type: TOAST_TYPE_WARNING,
          }),
        );
        onReinstateNeedsExpiry(link);
        return;
      }
      dispatch(showToastNotification({ content: t("Link enabled"), type: TOAST_TYPE_SUCCESS }));
      // Back to the tab the link now lives on, with the selection cleared — it is no longer on the
      // Revoked list the toolbar was acting against.
      setTab("active");
      setSelectedId(null);
      onMutated();
    } finally {
      setReinstating(false);
    }
  };

  const tabButton = (key: "active" | "revoked", label: string, count: number) => (
    <button
      type="button"
      onClick={() => {
        setTab(key);
        // Selection is per-tab: keeping a row selected across a switch would leave the toolbar
        // acting on a link that is no longer on screen.
        setSelectedId(null);
      }}
      className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-semibold ${
        tab === key ? "border-system_colour_1 text-primary_text" : "border-transparent text-text_light"
      }`}
    >
      <span>{label}</span>
      <span className="rounded-full bg-light_gray px-2 py-0.5 text-xs">{count}</span>
    </button>
  );

  /**
   * QA r3 #4: the actions live in the GRID's own header toolbar, standard one-primary convention.
   * Active: Create link is the tinted primary (creation is this dialog's main act), the row actions
   * plain. Revoked: View Stats primary, Enable Link plain (matching the register). Editing or
   * copying a dead link is meaningless, so those are absent on Revoked rather than disabled —
   * unlike the selection-dependent state, which is disabled, not hidden.
   */
  const toolbarItems = useMemo<(ToolbarItem & { name?: string; buttonType?: string })[]>(() => {
    if (tab === "revoked") {
      return [
        {
          id: "stats",
          name: t("View Stats"),
          prefixIcon: "activity",
          buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
          disabled: !selected,
        },
        {
          id: "enable",
          name: t("Enable Link"),
          prefixIcon: "refresh",
          buttonType: BUTTON_TYPES.TEXT_BUTTON,
          disabled: !selected || reinstating,
        },
      ];
    }
    return [
      {
        id: "create",
        name: t("Create link"),
        prefixIcon: "add",
        buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
        ["data-testid"]: "createExternalLinkFromManage",
      },
      {
        id: "edit",
        name: t("Edit"),
        prefixIcon: "editProfile",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: !selected,
      },
      {
        id: "stats",
        name: t("View Stats"),
        prefixIcon: "activity",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: !selected,
      },
      {
        id: "revoke",
        name: t("Revoke"),
        prefixIcon: "delete",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: !selected,
      },
    ];
  }, [tab, selected, reinstating, t]);

  const handleToolbarClick = (command: string) => {
    if (command === "create") {
      onCreate();
      return;
    }
    if (!selected) return;
    if (command === "edit") onEdit(selected);
    else if (command === "stats") onStats(selected);
    else if (command === "revoke") onRevoke(selected);
    else if (command === "enable") handleReinstate(selected);
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width={LINK_DIALOG_WIDTH}
      headerTitle={
        <div className="flex min-w-0 items-center gap-2">
          <Text className="truncate">{t("Manage shared links")}</Text>
          <Text className="truncate text-sm font-normal text-text_light">{untranslated(`— ${target.name}`)}</Text>
        </div>
      }
      onCancel={onClose}
      modelContent={
        <div className="flex flex-col">
          <div className="flex items-center gap-6 border-b border-stroke_primary">
            {tabButton("active", t("Active"), activeLinks.length)}
            {tabButton("revoked", t("Revoked"), revokedLinks.length)}
          </div>

          <div className="h-96">
            {loading ? (
              <div className="px-3 py-10 text-center text-sm text-text_light">{t("Loading...")}</div>
            ) : (
              <SlickGrid1
                key={tab}
                ref={gridRef}
                dataset={gridRows}
                columnDefinitions={columnDefinitions}
                toolbar={toolbarItems}
                toolbarClickHandler={handleToolbarClick}
                onSelectedRowIdsChanged={handleSelectionChange}
                enableMultiSelect={false}
                enableSelectAllCheckbox={false}
                selectAtmostOneCheckBoxOnRowClick
                searchFields={["name", "createdByName"]}
                searchPlaceholder={t("Search")}
                rowUnitLabel={t("links")}
                disableMenuOptions
              />
            )}
          </div>
        </div>
      }
    />
  );
}

export default ManageExternalLinksModal;
